/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { AlertTriangle, Building2, CheckCircle2, IndianRupee } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChartCard, type BarDatum } from '../../components/ui/bar-chart.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { StatCard } from '../../components/ui/stat-card.js';
import { type SaDashboard } from '../../lib/api/sa.js';
import { useSaDashboard } from '../../lib/queries/sa.js';
import { formatInr } from '../../lib/utils/money.js';
import { DashboardCard } from '../admin/dashboard-card.js';

const EMPTY: SaDashboard = {
  totalClinics: 0,
  activeClinics: 0,
  mrrPaise: 0,
  health: { red: 0, amber: 0, green: 0 },
  topBySessions: [],
  topByRevenue: [],
};

const HEALTH_ROWS = [
  {
    key: 'green',
    labelKey: 'sa.dashboard.healthGreen',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
  },
  { key: 'amber', labelKey: 'sa.dashboard.healthAmber', dot: 'bg-amber-500', bar: 'bg-amber-500' },
  { key: 'red', labelKey: 'sa.dashboard.healthRed', dot: 'bg-rose-500', bar: 'bg-rose-500' },
] as const;

/** RAG breakdown of clinic health — coloured dots + counts + a proportion bar. */
function PlatformHealthCard({ health }: { readonly health: SaDashboard['health'] }): JSX.Element {
  const { t } = useTranslation();
  const total = health.green + health.amber + health.red;
  return (
    <DashboardCard
      title={t('sa.dashboard.platformHealth')}
      subtitle={t('sa.dashboard.platformHealthSub')}
    >
      <div className="space-y-3">
        {total > 0 && (
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            {HEALTH_ROWS.map((row) => {
              const count = health[row.key];
              return count > 0 ? (
                <div
                  key={row.key}
                  className={row.bar}
                  style={{ width: `${String((count / total) * 100)}%` }}
                />
              ) : null;
            })}
          </div>
        )}
        <ul className="space-y-2">
          {HEALTH_ROWS.map((row) => (
            <li key={row.key} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                <span className={`h-2.5 w-2.5 rounded-full ${row.dot}`} aria-hidden="true" />
                {t(row.labelKey)}
              </span>
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {t('sa.dashboard.clinicsUnit', { count: health[row.key] })}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </DashboardCard>
  );
}

/** Super Admin → Dashboard. Platform-wide KPIs + top-clinic charts. */
export function SaDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const query = useSaDashboard();
  const data = query.data ?? EMPTY;

  const atRisk = data.health.amber + data.health.red;

  const bySessions: BarDatum[] = data.topBySessions.map((row) => ({
    label: row.name,
    value: row.sessions,
  }));
  const byRevenue: BarDatum[] = data.topByRevenue.map((row) => ({
    label: row.name,
    value: Math.round(row.revenuePaise / 100),
  }));

  return (
    <div className="p-8">
      <PageHeader title={t('sa.dashboard.title')} subtitle={t('sa.dashboard.subtitle')} />

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('sa.dashboard.empty')}
      >
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={Building2}
            label={t('sa.dashboard.totalClinics')}
            value={String(data.totalClinics)}
            accent="bg-[var(--accent)]"
          />
          <StatCard
            icon={CheckCircle2}
            label={t('sa.dashboard.activeClinics')}
            value={String(data.activeClinics)}
            accent="bg-emerald-600"
          />
          <StatCard
            icon={IndianRupee}
            label={t('sa.dashboard.mrr')}
            value={formatInr(data.mrrPaise)}
            accent="bg-sky-600"
          />
          <StatCard
            icon={AlertTriangle}
            label={t('sa.dashboard.atRisk')}
            value={String(atRisk)}
            accent="bg-orange-500"
            hint={t('sa.dashboard.atRiskHint')}
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <PlatformHealthCard health={data.health} />
          <BarChartCard title={t('sa.dashboard.topBySessions')} data={bySessions} color="#4f46e5" />
          <BarChartCard title={t('sa.dashboard.topByRevenue')} data={byRevenue} color="#4f46e5" />
        </div>
      </QueryState>
    </div>
  );
}
